import { useEffect, useState } from 'react';
import { FileText, Mail, Download, ExternalLink } from 'lucide-react';
import { API } from '../../lib/api';

const emailKinds = [
  { key: 'invoice', label: 'New Invoice', description: 'Sent when admin confirms a draft invoice' },
  { key: 'resend', label: 'Updated Invoice (Resend)', description: 'Sent after editing an already-sent invoice' },
  { key: 'payment', label: 'Payment Received', description: 'Sent when a payment is recorded' },
  { key: 'reminder', label: 'Overdue Reminder', description: 'Sent when invoice is past due' },
];

export default function Samples() {
  const [activeKind, setActiveKind] = useState('invoice');
  const [emailHtml, setEmailHtml] = useState('');
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${API}/admin/samples/email?kind=${activeKind}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const html = await resp.text();
        setEmailHtml(html);
      } catch (e) {
        setEmailHtml('<p>Failed to load sample</p>');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeKind, token]);

  const downloadSamplePdf = async () => {
    const resp = await fetch(`${API}/admin/samples/invoice-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'OttoMed-Sample-Invoice.pdf';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openSamplePdf = () => {
    // Open PDF inline in a new tab (requires token header, so we do this via a fetch + blob URL)
    fetch(`${API}/admin/samples/invoice-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      });
  };

  return (
    <div className="fade-in space-y-8" data-testid="samples-page">
      <div>
        <h1 className="text-3xl font-semibold">Samples</h1>
        <p className="text-sm text-[#6b7280] mt-1">
          Preview how your invoice PDF and each customer email will look. Uses dummy data for safety.
        </p>
      </div>

      {/* Invoice PDF Sample */}
      <section className="card p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#fde8e8] flex items-center justify-center">
              <FileText size={20} className="text-[#d92027]" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Invoice PDF Template</h2>
              <p className="text-sm text-[#6b7280]">
                Shows how every invoice looks — with your logo, business info, line items, balance, and payment link.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={openSamplePdf}
              className="btn-ghost flex items-center gap-2"
              data-testid="open-sample-pdf-btn"
            >
              <ExternalLink size={14} /> Open
            </button>
            <button
              onClick={downloadSamplePdf}
              className="btn-brand flex items-center gap-2"
              data-testid="download-sample-pdf-btn"
            >
              <Download size={14} /> Download
            </button>
          </div>
        </div>

        <div className="border border-[#e7e7e7] rounded-lg overflow-hidden bg-[#fafafa]" style={{ height: '820px' }}>
          <iframe
            title="Sample Invoice PDF"
            src={`${API}/admin/samples/invoice-pdf#toolbar=0&navpanes=0`}
            className="w-full h-full"
            style={{ border: 'none' }}
            // Note: iframe can't send Authorization header. The backend requires admin token,
            // so we fall back to showing the button links if iframe fails.
          />
        </div>
        <p className="text-xs text-[#6b7280] italic">
          If the preview above appears blank, click <strong>Open</strong> or <strong>Download</strong> — the PDF endpoint
          is token-protected and iframes can't send auth headers.
        </p>
      </section>

      {/* Email Samples */}
      <section className="card p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#fde8e8] flex items-center justify-center">
            <Mail size={20} className="text-[#d92027]" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Email Templates</h2>
            <p className="text-sm text-[#6b7280]">
              These are the exact emails your customers receive. Logo and business footer are included in every email.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-[#f3f3f3] pb-3">
          {emailKinds.map((k) => (
            <button
              key={k.key}
              onClick={() => setActiveKind(k.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeKind === k.key
                  ? 'bg-[#d92027] text-white'
                  : 'bg-[#fafafa] text-[#1f1f1f] hover:bg-[#f3f3f3]'
              }`}
              data-testid={`sample-email-tab-${k.key}`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="text-sm text-[#6b7280] italic">
          {emailKinds.find((k) => k.key === activeKind)?.description}
        </div>

        <div className="border border-[#e7e7e7] rounded-lg overflow-hidden bg-[#f5f5f5]">
          {loading ? (
            <div className="h-[640px] flex items-center justify-center text-sm text-[#6b7280]">Loading…</div>
          ) : (
            <iframe
              title="Sample Email"
              srcDoc={emailHtml}
              className="w-full"
              style={{ border: 'none', height: '820px' }}
            />
          )}
        </div>
      </section>
    </div>
  );
}
